import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Flag to prevent multiple logout toasts
let isLoggingOut = false;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 (Unauthorized) - Token expired or invalid
    if (error.response?.status === 401 && !isLoggingOut) {
      isLoggingOut = true;

      // Clear all auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('spirit:user');

      // Show friendly message to user
      toast.error('Your session has expired. Please login again.', {
        id: 'auth:expired',
        duration: 4000,
        position: 'top-right',
      });

      // Dispatch logout event for global state management
      window.dispatchEvent(new Event('auth:logout'));

      // Redirect to home page after a short delay
      setTimeout(() => {
        window.location.href = '/';
        isLoggingOut = false;
      }, 1500);

      return Promise.reject(error);
    }

    // Handle 403 (Forbidden) - No permission
    if (error.response?.status === 403) {
      toast.error('You do not have permission to perform this action.', {
        id: 'auth:forbidden',
        duration: 4000,
        position: 'top-right',
      });
      return Promise.reject(error);
    }

    // Try to show descriptive message returned by backend when available
    try {
      const resp = error.response;
      let message = null;
      if (resp) {
        if (resp.data) {
          // common patterns: { message: '...', error: '...' }
          message = resp.data.message || resp.data.error || (typeof resp.data === 'string' ? resp.data : null);
        }
        // fallback to status text
        if (!message && resp.statusText) message = resp.statusText;
      }
      if (!message) message = error.message || 'Request failed';

      // Show/replace the message to the user in a toast (reuse same id)
      toast.error(message, { id: 'api:error', duration: 5000, position: 'top-right' });
    } catch (e) {
      // ignore toast errors
    }

    return Promise.reject(error);
  }
);

export default api;
